/**
 * PodiumJourneyPage.jsx — Diplomexa Podium Journey
 * --------------------------------------------------
 * Sprint LMS-Gamify — the central, premium gamification page.
 *
 * Composes:
 *   • Gamification Card  (overall rank + points + next-rank progress)
 *   • Learning Streak Widget (current + longest streak)
 *   • Badge Gallery (earned + unearned, tiered)
 *   • Achievement Timeline (last 20 awards)
 *   • Global Delegate Rank Leaderboard
 *   • Podium Points Catalogue (admin-configurable rules, public read-only)
 *
 * Hard rules (visual + content):
 *   • Diploks are never shown here. This page is purely Podium Points.
 *   • Aspirational language: "Global Delegate Rank", "Readiness Streak",
 *     "Podium Journey", "Diplomatic Achievement", "Leadership Milestone".
 *   • Premium, never childish.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import {
  getGamificationSummary, getBadgeCatalogue, getPodiumRules,
  getLeaderboard, formatApiError,
} from "../../lib/gamificationApi";
import { toast } from "sonner";
import {
  Trophy, Flame, Award, Sparkles, Crown, Star, Globe2, Compass,
  ArrowRight, CheckCircle2, Calendar, ListChecks, Lock, ChevronRight,
  Medal, Gem, Diamond,
} from "lucide-react";

const RANK_ICON = { Sparkles, Star, Award, Trophy, Crown, Globe2, Gem, Diamond };
const TIER_COLOR = {
  bronze:   { ring: "ring-amber-700/40",  bg: "bg-amber-50",  text: "text-amber-800",  glow: "from-amber-200 to-amber-100" },
  silver:   { ring: "ring-slate-400/50",  bg: "bg-slate-50",  text: "text-slate-700",  glow: "from-slate-200 to-slate-100" },
  gold:     { ring: "ring-[#D4AF37]/60",  bg: "bg-amber-50",  text: "text-[#0A192F]",  glow: "from-[#D4AF37]/30 to-amber-50" },
  platinum: { ring: "ring-indigo-400/50", bg: "bg-indigo-50", text: "text-indigo-800", glow: "from-indigo-200 to-indigo-50" },
  diamond:  { ring: "ring-cyan-400/60",   bg: "bg-cyan-50",   text: "text-cyan-800",   glow: "from-cyan-200 to-sky-100" },
  legend:   { ring: "ring-fuchsia-500/60",bg: "bg-fuchsia-50",text: "text-fuchsia-800",glow: "from-fuchsia-200 via-rose-200 to-amber-100" },
};

const STREAK_MILESTONES = [
  { days: 7,   label: "Silver",    points: 75   },
  { days: 14,  label: "Gold",      points: 150  },
  { days: 30,  label: "Master",    points: 300  },
  { days: 60,  label: "Champion",  points: 600  },
  { days: 100, label: "Legend",    points: 1000 },
];

export default function PodiumJourneyPage() {
  const [summary, setSummary] = useState(null);
  const [badges, setBadges] = useState(null);
  const [rules, setRules] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, b, r, lb] = await Promise.all([
          getGamificationSummary(),
          getBadgeCatalogue(),
          getPodiumRules(),
          getLeaderboard(15),
        ]);
        setSummary(s); setBadges(b); setRules(r); setLeaderboard(lb);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !summary) {
    return (
      <DashboardLayout>
        <div className="grid h-72 place-items-center text-sm text-slate-500">Loading your Podium Journey…</div>
      </DashboardLayout>
    );
  }

  const rank = summary.rank || { name: "Academy Novice", icon: "Sparkles" };
  const nextRank = summary.next_rank;
  const points = summary.podium_points || 0;
  const RankIcon = RANK_ICON[rank.icon] || Sparkles;
  const progressPct = nextRank
    ? Math.min(100, Math.round(((points - (rank.min_points || 0))
        / Math.max(1, (nextRank.min_points - (rank.min_points || 0)))) * 100))
    : 100;

  return (
    <DashboardLayout title="Podium Journey">
      <div data-testid="podium-journey-page" className="space-y-6 pb-12">
        {/* Cinematic hero */}
        <section
          data-testid="podium-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-8 shadow-2xl text-white"
        >
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
          <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
                <Trophy size={12} /> Diplomexa · Podium Journey
              </p>
              <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
                {rank.name}
              </h1>
              <p className="mt-2 max-w-xl text-sm text-blue-100">
                Your free engagement reward for showing up, completing courses, attending live sessions and earning certificates.
                <span className="block mt-1 text-[#D4AF37] font-semibold">
                  Podium Points are completely separate from Diploks — they unlock badges, ranks and milestones, never discounts.
                </span>
              </p>
              <div className="mt-5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#D4AF37] font-bold">
                    {points.toLocaleString()} Podium Points
                  </span>
                  {nextRank ? (
                    <span className="text-blue-100">
                      {nextRank.points_to_go} to <span className="font-semibold text-white">{nextRank.name}</span>
                    </span>
                  ) : (
                    <span className="text-emerald-300 font-semibold">Top rank achieved 🏆</span>
                  )}
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-white/15 overflow-hidden">
                  <div
                    data-testid="podium-rank-progress"
                    className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/dashboard/lms" className="inline-flex items-center gap-1 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20">
                  <Compass size={11} /> Learning Academy
                </Link>
                <Link to="/dashboard/lms/mun-prep" className="inline-flex items-center gap-1 rounded-lg bg-[#D4AF37] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]">
                  <Sparkles size={11} /> MUN Prep Hub
                </Link>
              </div>
            </div>

            {/* Rank crest */}
            <div className="lg:col-span-5 flex items-center justify-center">
              <div data-testid="podium-rank-crest" className="relative">
                <div className="absolute -inset-6 rounded-full bg-[#D4AF37]/20 blur-2xl" />
                <div className="relative h-44 w-44 rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] grid place-items-center border-4 border-[#D4AF37]/60 shadow-2xl">
                  <RankIcon size={64} className="text-[#D4AF37]" />
                </div>
                <p className="mt-3 text-center text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
                  Global Delegate Rank
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* KPIs row */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi icon={Trophy} testid="podium-kpi-points" label="Podium Points" value={points.toLocaleString()} />
          <Kpi icon={Flame}  testid="podium-kpi-streak" label="Current Streak"  value={`${summary.streak?.current_days || 0}d`} />
          <Kpi icon={Award}  testid="podium-kpi-badges" label="Badges earned"   value={(summary.badges || []).length} />
          <Kpi icon={Star}   testid="podium-kpi-longest" label="Longest Streak" value={`${summary.streak?.longest_days || 0}d`} />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <main className="lg:col-span-8 space-y-6">
            {/* Streak widget */}
            <StreakWidget streak={summary.streak} />

            {/* Badge gallery */}
            <BadgeGallery badges={badges?.badges || []} />

            {/* Podium Points catalogue */}
            <PodiumRulesCatalogue rules={rules?.rules || []} />
          </main>

          <aside className="lg:col-span-4 space-y-6">
            {/* Achievement timeline */}
            <AchievementTimeline events={summary.achievements_timeline || []} />

            {/* Leaderboard */}
            <Leaderboard rows={leaderboard?.leaderboard || []} />

            {/* Compliance footer (Podium ≠ Diploks) */}
            <PolicyCard />
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============================================================================
// Sub-components
// =============================================================================
function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        <Icon size={12} className="text-[#0F4C81]" /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-[#0A192F] tabular-nums">{value}</div>
    </div>
  );
}

function StreakWidget({ streak }) {
  const cur = streak?.current_days || 0;
  const longest = streak?.longest_days || 0;
  const next = STREAK_MILESTONES.find((m) => m.days > cur)
              || STREAK_MILESTONES[STREAK_MILESTONES.length - 1];
  const previousMs = STREAK_MILESTONES
    .filter((m) => m.days <= cur)
    .pop() || { days: 0, label: "Starting" };
  const pct = next.days === previousMs.days
    ? 100
    : Math.min(100, Math.round(((cur - previousMs.days)
        / (next.days - previousMs.days)) * 100));
  return (
    <section
      data-testid="streak-widget"
      className="relative overflow-hidden rounded-2xl border border-rose-200/60 bg-gradient-to-br from-rose-50 via-amber-50/50 to-white p-5 shadow-sm"
    >
      <div className="absolute -top-12 -right-10 h-32 w-32 rounded-full bg-rose-300/30 blur-2xl" />
      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-rose-600 flex items-center gap-1">
            <Flame size={11} /> Readiness Streak
          </p>
          <h2 className="mt-1 text-2xl font-bold text-[#0A192F]">
            {cur} day{cur === 1 ? "" : "s"}
          </h2>
          <p className="mt-1 text-xs text-slate-600">
            {cur === 0
              ? "Show up today to start your Diplomatic streak."
              : <>Next milestone: <span className="font-semibold text-rose-700">{next.label}</span> at <span className="font-semibold text-rose-700">{next.days} days</span> · earns <span className="font-semibold text-rose-700">+{next.points} pts</span></>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Longest</p>
          <p data-testid="streak-longest" className="text-lg font-bold text-[#0A192F]">{longest}d</p>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">
          <span data-testid="streak-prev-milestone">{previousMs.days}d</span>
          <span data-testid="streak-next-milestone">{next.days}d</span>
        </div>
        <div className="mt-1 h-2.5 rounded-full bg-rose-100 overflow-hidden">
          <div
            data-testid="streak-progress"
            className="h-full rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-amber-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div data-testid="streak-milestones" className="mt-3 flex flex-wrap gap-1.5">
          {STREAK_MILESTONES.map((m) => {
            const reached = cur >= m.days;
            return (
              <span
                key={m.days}
                data-testid={`streak-pill-${m.days}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${
                  reached
                    ? "border-rose-300 bg-rose-100 text-rose-700"
                    : "border-slate-200 bg-white text-slate-500"
                }`}
                title={`${m.days}-day · ${m.label} streak · +${m.points} pts`}
              >
                {reached ? <CheckCircle2 size={9} /> : <Lock size={9} />}
                {m.days}d
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BadgeGallery({ badges }) {
  return (
    <section
      data-testid="badge-gallery"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <Award size={14} className="text-[#D4AF37]" /> Diplomatic Achievements
      </h2>
      <p className="mt-1 text-[11px] text-slate-500">
        Earned badges are full-colour; locked ones unlock as you progress.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {(badges || []).map(b => {
          const c = TIER_COLOR[b.tier] || TIER_COLOR.gold;
          const earned = !!b.earned;
          return (
            <div
              key={b.slug}
              data-testid={`badge-${b.slug}`}
              className={`relative rounded-xl border p-3 text-center transition ${
                earned
                  ? `bg-gradient-to-br ${c.glow} ring-1 ${c.ring} border-transparent shadow`
                  : "border-dashed border-slate-300 bg-slate-50/60 opacity-60 grayscale"
              }`}
              title={b.description}
            >
              <div className={`mx-auto grid h-12 w-12 place-items-center rounded-full ${
                earned ? "bg-white shadow" : "bg-slate-100"
              }`}>
                {earned ? <Medal size={22} className={c.text} /> : <Lock size={18} className="text-slate-400" />}
              </div>
              <p className={`mt-2 text-[11px] font-bold uppercase tracking-wider ${earned ? c.text : "text-slate-500"}`}>
                {b.tier}
              </p>
              <p className="mt-0.5 text-[12px] font-semibold text-[#0A192F] line-clamp-2">
                {b.name}
              </p>
              <p className="mt-1 text-[10px] text-slate-500 line-clamp-2">
                {b.description}
              </p>
              {earned ? (
                <CheckCircle2 size={12} className="absolute top-1.5 right-1.5 text-emerald-500" />
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PodiumRulesCatalogue({ rules }) {
  return (
    <section
      data-testid="podium-rules-catalogue"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <ListChecks size={14} className="text-[#D4AF37]" /> How to earn Podium Points
      </h2>
      <p className="mt-1 text-[11px] text-slate-500">
        Free engagement rewards. No purchase required — and no Diploks discount, ever.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
        {rules.map(r => (
          <div key={r.action_key}
                data-testid={`podium-rule-${r.action_key}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-[#0A192F]">{r.label}</p>
              <p className="text-[10px] text-slate-500">
                Daily cap {r.daily_cap} · weekly {r.weekly_cap}
              </p>
            </div>
            <span className="rounded-full bg-[#0A192F] px-2 py-0.5 text-[10px] font-bold text-[#D4AF37]">
              +{r.points} pts
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AchievementTimeline({ events }) {
  if (!events?.length) {
    return (
      <section data-testid="achievement-timeline" className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1">
          <Calendar size={11} /> Achievement Timeline
        </p>
        <p className="mt-2 text-xs text-slate-500">
          You haven&apos;t earned any points yet. Complete a lesson or join a live class to start.
        </p>
      </section>
    );
  }
  return (
    <section data-testid="achievement-timeline" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0F4C81] flex items-center gap-1">
        <Calendar size={11} /> Achievement Timeline
      </p>
      <ol className="mt-3 space-y-2 max-h-80 overflow-auto">
        {events.map((e, i) => (
          <li key={e.id || i} data-testid={`achievement-${i}`}
              className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <div className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-[#0A192F]">
                +{e.points} pts · {humanizeAction(e.action_key)}
              </p>
              <p className="text-[10px] text-slate-500">{(e.created_at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
            <ChevronRight size={12} className="text-slate-300 mt-1" />
          </li>
        ))}
      </ol>
    </section>
  );
}

function Leaderboard({ rows }) {
  return (
    <section data-testid="podium-leaderboard" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0F4C81] flex items-center gap-1">
        <Trophy size={11} /> Global Delegate Rank — Top 15
      </p>
      {!rows?.length ? (
        <p className="mt-3 text-xs text-slate-500">No leaderboard data yet — be the first to score.</p>
      ) : (
        <ol className="mt-3 space-y-1.5">
          {rows.map(r => (
            <li key={r.user_id} data-testid={`leaderboard-${r.position}`}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                  r.position <= 3
                    ? "bg-gradient-to-r from-amber-50 to-white border border-[#D4AF37]/30"
                    : "border border-slate-200"
                }`}>
              <span className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold ${
                r.position === 1 ? "bg-[#D4AF37] text-[#0A192F]" :
                r.position === 2 ? "bg-slate-300 text-[#0A192F]" :
                r.position === 3 ? "bg-amber-700 text-white" :
                "bg-slate-100 text-slate-600"
              }`}>{r.position}</span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[#0A192F] truncate">{r.name}</p>
                <p className="text-[10px] text-slate-500">{r.rank?.name || "—"}</p>
              </div>
              <span className="text-xs font-bold text-[#0F4C81] tabular-nums">{r.points.toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function PolicyCard() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <p className="text-[11px] text-slate-600 leading-relaxed">
        <span className="font-semibold text-[#0A192F]">Podium Points</span> are
        a free engagement reward. They do not convert to Diploks, do not
        discount premium features, and never expire.
        <span className="block mt-1">
          <span className="font-semibold text-[#0A192F]">Diploks</span> are
          paid platform credits used for premium AI tools — fully separate, by design.
        </span>
      </p>
    </section>
  );
}

function humanizeAction(key) {
  if (!key) return "Achievement";
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}
